import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Container } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Navbar from "../../../Navbar";
import { useFechas } from "../context/FechaContext";
import { logEventos } from "../../../../services/services";
import useMqttSubscription from "../../../../Hooks/useMqttSubscription";
import usePedidosData from "./hooks/usePedidosData";
import usePendientesFacturacionCount from "./hooks/usePendientesFacturacionCount";
import useAutoRefresh from "./hooks/useAutoRefresh";
import useAccionesPedido from "./hooks/useAccionesPedido";
import useSeleccionPedidos from "./hooks/useSeleccionPedidos";
import useEsMobile from "./hooks/useEsMobile";
import { pedidoCoincideConBusqueda } from "./utils/busquedaPedidos";
import EncabezadoPedidos from "./components/EncabezadoPedidos";
import NavegacionTabs from "./components/NavegacionTabs";
import PedidosPorSucursal from "./components/PedidosPorSucursal";
import ModalesPedidoHost from "./components/ModalesPedidoHost";

// Reemplazo autocontenido de TabPedidos + PedidosEnvasado: pedidos en
// tarjetas agrupadas por sucursal (una sección colapsable por sucursal), para
// que un operador pueda separar de un vistazo el trabajo de cada sucursal en
// vez de escanear una lista larga mezclada. Todas las acciones (asignar,
// anular, editar, facturar, etc.) reutilizan los modales del módulo original.
const VistaPedidosEnvasado = () => {
  const { desde, hasta } = useFechas();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const esMobile = useEsMobile();
  const seleccionCards = useSeleccionPedidos();

  const { pedidos, loading, lastUpdated, refetch } = usePedidosData(activeTab, desde, hasta);
  const { contador, refetchContador } = usePendientesFacturacionCount(desde, hasta);

  // Búsqueda por cliente, dirección o chofer: filtra lo ya cargado de la
  // pestaña activa, sin volver a golpear el backend, y se mantiene al
  // cambiar de pestaña.
  const [busqueda, setBusqueda] = useState("");
  const pedidosFiltrados = useMemo(
    () => pedidos.filter((pedido) => pedidoCoincideConBusqueda(pedido, busqueda)),
    [pedidos, busqueda]
  );

  const actualizarTodo = useCallback(() => {
    refetch();
    refetchContador();
  }, [refetch, refetchContador]);

  const { modales, pedidoActivo, abrirModal, cerrarModal, hayModalAbierto } = useAccionesPedido(actualizarTodo);

  const [asignarEnGrupo, setAsignarEnGrupo] = useState({ abierto: false, pedidos: [] });

  useEffect(() => {
    logEventos("acceso al menú Pedidos", "Envasado", "Pedidos", localStorage.getItem("user"));
  }, []);

  useEffect(() => {
    // Cambiar de pestaña invalida cualquier selección previa.
    seleccionCards.limpiarSeleccion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "F5") {
        event.preventDefault();
        abrirModal("nuevoPedido");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [abrirModal]);

  // Actualización en vivo: el backend publica cambios de pedidos por MQTT,
  // así que refrescamos apenas llega un mensaje en vez de esperar el poll.
  // El poll de 30s se deja como respaldo por si la conexión MQTT se cae.
  useMqttSubscription("netizar/pedidosenvasados", () => {
    if (!hayModalAbierto) actualizarTodo();
  });

  useAutoRefresh(actualizarTodo, 30000, hayModalAbierto);

  const abrirAsignarGrupo = (pedidosSeleccionados) => {
    if (pedidosSeleccionados.length === 0) return;
    setAsignarEnGrupo({ abierto: true, pedidos: pedidosSeleccionados });
  };

  const cerrarAsignarGrupo = (borrar) => {
    if (borrar) seleccionCards.limpiarSeleccion();
    setAsignarEnGrupo({ abierto: false, pedidos: [] });
    actualizarTodo();
  };

  return (
    <div>
      <Navbar />
      <Container maxWidth="xl" disableGutters sx={{ mb: 2, mt: 1 }}>
        <EncabezadoPedidos
          onNuevoPedido={() => abrirModal("nuevoPedido")}
          onVerMapa={() => navigate("/mapapedidosenvasado", { state: { pedidos } })}
          onRefrescar={actualizarTodo}
          loading={loading}
          lastUpdated={lastUpdated}
          esMobile={esMobile}
          busqueda={busqueda}
          onBusquedaChange={setBusqueda}
        />

        <Box sx={{ px: 1 }}>
          <NavegacionTabs
            activeTab={activeTab}
            onChange={setActiveTab}
            cantidadActiva={pedidosFiltrados.length}
            contadorFacturacion={contador}
            esMobile={esMobile}
          />
        </Box>

        <PedidosPorSucursal
          pedidos={pedidosFiltrados}
          loading={loading}
          activeTab={activeTab}
          abrirModal={abrirModal}
          seleccion={seleccionCards}
          onAsignarGrupo={abrirAsignarGrupo}
          busqueda={busqueda}
        />

        <ModalesPedidoHost
          modales={modales}
          pedidoActivo={pedidoActivo}
          cerrarModal={cerrarModal}
          asignarEnGrupoAbierto={asignarEnGrupo.abierto}
          rowsAsignarGrupo={asignarEnGrupo.pedidos.map((p) => ({ original: p }))}
          onCerrarAsignarGrupo={cerrarAsignarGrupo}
        />
      </Container>
    </div>
  );
};

export default VistaPedidosEnvasado;
